import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../App.css';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { BookOpen, Sparkles, Users, Map, Copy, Check, Loader2, Settings as SettingsIcon, LogIn, Heart } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import DonationModal from '../components/DonationModal';
import AdSense from '../components/AdSense';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Home() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  const [userInput, setUserInput] = useState('');
  const [bibleVersion, setBibleVersion] = useState('NIV');
  const [studyLength, setStudyLength] = useState('Medium');
  const [studyType, setStudyType] = useState('Devotional');
  const [outputLanguage, setOutputLanguage] = useState('English');
  const [isLoading, setIsLoading] = useState(false);
  const [studyData, setStudyData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);

  const handleGenerate = async () => {
    if (!userInput.trim()) {
      toast({
        title: "Input Required",
        description: "Please enter a Bible verse or topic to generate a study.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/generate-study`, {
        userInput,
        bibleVersion,
        studyLength,
        studyType,
        outputLanguage
      });
      
      setStudyData(response.data);
      
      setTimeout(() => {
        document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
      toast({
        title: "Study Generated!",
        description: "Your Bible study has been created successfully."
      });
      
    } catch (error) {
      console.error('Error generating study:', error);
      toast({
        title: "Generation Failed",
        description: error.response?.data?.detail || "Failed to generate study. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyAll = () => {
    if (!studyData) return;

    let textToCopy = `
ITS WRITTEN AI BIBLE STUDY
${'='.repeat(50)}

INPUT: ${userInput}
VERSION: ${bibleVersion}
LENGTH: ${studyLength}
TYPE: ${studyType}
LANGUAGE: ${outputLanguage}

${'='.repeat(50)}
SHORT SUMMARY
${'='.repeat(50)}
${studyData.shortSummary}

${'='.repeat(50)}
STUDY OUTLINE
${'='.repeat(50)}
${studyData.studyOutline.map((item, i) => `${i + 1}. ${item}`).join('\n')}

${'='.repeat(50)}
KEY INSIGHTS
${'='.repeat(50)}
${studyData.keyInsights.map((item, i) => `${i + 1}. ${item}`).join('\n')}

${'='.repeat(50)}
PRACTICAL APPLICATIONS
${'='.repeat(50)}
${studyData.practicalApplications.map((item, i) => `${i + 1}. ${item}`).join('\n')}

${'='.repeat(50)}
DISCUSSION QUESTIONS
${'='.repeat(50)}
${studyData.discussionQuestions.map((item, i) => `${i + 1}. ${item}`).join('\n')}

${'='.repeat(50)}
CLOSING PRAYER
${'='.repeat(50)}
${studyData.closingPrayer}
`;

    if (studyType === 'Sermon / Deep Study' && studyData.originalLanguageWordStudy && studyData.sermonScript) {
      textToCopy += `
${'='.repeat(50)}
ORIGINAL LANGUAGE WORD STUDY
${'='.repeat(50)}
${studyData.originalLanguageWordStudy.map((word, i) => `
${i + 1}. ${word.word}
   Meaning: ${word.meaning}
   Significance: ${word.significance}`).join('\n')}

${'='.repeat(50)}
SERMON SCRIPT (DRAFT)
${'='.repeat(50)}

INTRODUCTION:
${studyData.sermonScript.introduction}

MAIN POINTS:
${studyData.sermonScript.mainPoints.map((point, i) => `
${i + 1}. ${point.title}
${point.content}`).join('\n')}

GOSPEL CONNECTION:
${studyData.sermonScript.gospelConnection}

CALL TO RESPONSE:
${studyData.sermonScript.callToResponse}
`;
    }

    textToCopy += `
${'='.repeat(50)}
Generated by Its Written AI - Because It Is Written
`;

    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Bible study copied to clipboard."
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="App">
      <Toaster />
      <DonationModal 
        isOpen={showDonationModal} 
        onClose={() => setShowDonationModal(false)} 
      />
      
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <img 
                src="/its-written-logo.png" 
                alt="Its Written AI Logo" 
                style={{ height: '50px', width: 'auto' }}
              />
              <h1 className="logo-text">Its Written AI</h1>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {isAuthenticated ? (
                <>
                  <span style={{ color: 'white', display: 'flex', alignItems: 'center', marginRight: '0.5rem' }}>
                    {user?.name}
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => setShowDonationModal(true)}
                    style={{ color: '#F0C96A' }}
                    className="donate-header-button"
                  >
                    <Heart style={{ width: '18px', height: '18px', marginRight: '0.5rem' }} />
                    Support
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/settings')}
                    style={{ color: 'white' }}
                  >
                    <SettingsIcon style={{ width: '18px', height: '18px', marginRight: '0.5rem' }} />
                    Settings
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    style={{ color: 'white' }}
                  >
                    <LogIn style={{ width: '18px', height: '18px', marginRight: '0.5rem' }} />
                    Login
                  </Button>
                  <Button
                    onClick={() => navigate('/signup')}
                    style={{ background: '#F0C96A', color: '#1F4B8F' }}
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <Sparkles className="badge-icon" />
              <span>AI-Powered Bible Study</span>
            </div>
            <h1 className="hero-title">
              Ask Anything.
              <span className="hero-title-accent"> It's Written.</span>
            </h1>
            <p className="hero-description">
              Enter any Bible verse or topic, and receive a comprehensive, Christ-centered study guide in seconds. Because It Is Written.
            </p>

            {/* Input Form */}
            <div className="input-card">
              <Textarea
                placeholder="Enter a Bible verse (e.g., John 3:16) or topic (e.g., faith, prayer, forgiveness)..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                className="input-textarea"
                rows={4}
              />
              
              <div className="input-options">
                <div className="option-group">
                  <label className="option-label">Bible Version</label>
                  <Select value={bibleVersion} onValueChange={setBibleVersion}>
                    <SelectTrigger className="select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KJV">KJV</SelectItem>
                      <SelectItem value="NIV">NIV</SelectItem>
                      <SelectItem value="ESV">ESV</SelectItem>
                      <SelectItem value="NKJV">NKJV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="option-group">
                  <label className="option-label">Study Length</label>
                  <Select value={studyLength} onValueChange={setStudyLength}>
                    <SelectTrigger className="select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Short">Short</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="option-group">
                  <label className="option-label">Study Type</label>
                  <Select value={studyType} onValueChange={setStudyType}>
                    <SelectTrigger className="select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Devotional">Devotional</SelectItem>
                      <SelectItem value="Small Group Study">Small Group Study</SelectItem>
                      <SelectItem value="Sermon / Deep Study">Sermon / Deep Study</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="option-group">
                  <label className="option-label">Output Language</label>
                  <Select value={outputLanguage} onValueChange={setOutputLanguage}>
                    <SelectTrigger className="select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Portuguese (Brazil)">Portuguese (Brazil)</SelectItem>
                      <SelectItem value="Spanish">Spanish</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                onClick={handleGenerate} 
                disabled={isLoading}
                className="generate-button"
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="button-icon animate-spin" />
                    Generating Study...
                  </>
                ) : (
                  <>
                    <Sparkles className="button-icon" />
                    Generate Study
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Callout Section */}
      <section className="donation-callout-section">
        <div className="container">
          <div className="donation-callout-card">
            <div className="donation-callout-content">
              <div className="donation-callout-icon">
                <Heart className="heart-icon-large" />
              </div>
              <div className="donation-callout-text">
                <h3 className="donation-callout-title">Support the Mission</h3>
                <p className="donation-callout-description">
                  Help Its Written AI stay free and bring God's Word to people around the world. 
                  Your donation helps us send daily Bible studies and devotional messages to thousands.
                </p>
              </div>
              <div className="donation-callout-action">
                <Button
                  onClick={() => setShowDonationModal(true)}
                  className="donate-callout-button"
                  size="lg"
                >
                  <Heart style={{ width: '18px', height: '18px', marginRight: '0.5rem' }} />
                  Donate
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      {studyData && (
        <section className="results" id="results">
          <div className="container">
            <div className="results-header">
              <h2 className="results-title">Your Bible Study</h2>
              <Button 
                onClick={handleCopyAll}
                variant="outline"
                className="copy-button"
              >
                {copied ? (
                  <>
                    <Check className="button-icon" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="button-icon" />
                    Copy All
                  </>
                )}
              </Button>
            </div>

            <div className="study-grid">
              <Card className="study-card">
                <CardHeader>
                  <CardTitle className="card-title">Short Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="card-text">{studyData.shortSummary}</p>
                </CardContent>
              </Card>

              <Card className="study-card">
                <CardHeader>
                  <CardTitle className="card-title">Study Outline</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="ordered-list">
                    {studyData.studyOutline.map((item, index) => (
                      <li key={index} className="list-item">{item}</li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              <Card className="study-card">
                <CardHeader>
                  <CardTitle className="card-title">Key Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="unordered-list">
                    {studyData.keyInsights.map((item, index) => (
                      <li key={index} className="list-item">{item}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="study-card">
                <CardHeader>
                  <CardTitle className="card-title">Practical Applications</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="unordered-list">
                    {studyData.practicalApplications.map((item, index) => (
                      <li key={index} className="list-item">{item}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="study-card">
                <CardHeader>
                  <CardTitle className="card-title">Discussion Questions</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="ordered-list">
                    {studyData.discussionQuestions.map((item, index) => (
                      <li key={index} className="list-item">{item}</li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              <Card className="study-card prayer-card">
                <CardHeader>
                  <CardTitle className="card-title">Closing Prayer</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="card-text prayer-text">{studyData.closingPrayer}</p>
                </CardContent>
              </Card>

              {/* Conditional Sermon Mode Sections */}
              {studyType === 'Sermon / Deep Study' && studyData.originalLanguageWordStudy && (
                <>
                  <Card className="study-card sermon-card">
                    <CardHeader>
                      <CardTitle className="card-title">Original Language Word Study</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="word-study-list">
                        {studyData.originalLanguageWordStudy.map((word, index) => (
                          <div key={index} className="word-study-item">
                            <h4 className="word-title">{word.word}</h4>
                            <p className="word-meaning"><strong>Meaning:</strong> {word.meaning}</p>
                            <p className="word-significance"><strong>Significance:</strong> {word.significance}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="study-card sermon-card">
                    <CardHeader>
                      <CardTitle className="card-title">Sermon Script (Draft)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="sermon-script">
                        <div className="sermon-section">
                          <h4 className="sermon-subtitle">Introduction</h4>
                          <p className="sermon-text">{studyData.sermonScript.introduction}</p>
                        </div>

                        <div className="sermon-section">
                          <h4 className="sermon-subtitle">Main Points</h4>
                          {studyData.sermonScript.mainPoints.map((point, index) => (
                            <div key={index} className="sermon-point">
                              <h5 className="point-title">{index + 1}. {point.title}</h5>
                              <p className="point-content">{point.content}</p>
                            </div>
                          ))}
                        </div>

                        <div className="sermon-section">
                          <h4 className="sermon-subtitle">Gospel Connection</h4>
                          <p className="sermon-text">{studyData.sermonScript.gospelConnection}</p>
                        </div>

                        <div className="sermon-section">
                          <h4 className="sermon-subtitle">Call to Response</h4>
                          <p className="sermon-text">{studyData.sermonScript.callToResponse}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Ad Unit - After Study Results */}
            <div style={{ marginTop: '3rem' }}>
              <AdSense 
                slot="1111111111"
                format="auto"
                responsive={true}
                className="study-result-ad"
              />
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <section className="info-section">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-number">1</div>
              <h3 className="feature-title">Enter Your Verse or Topic</h3>
              <p className="feature-description">
                Type in any Bible verse reference or spiritual topic you'd like to explore.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-number">2</div>
              <h3 className="feature-title">Choose Your Preferences</h3>
              <p className="feature-description">
                Select your preferred Bible version, study length, study type, and output language.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-number">3</div>
              <h3 className="feature-title">Receive Your Study</h3>
              <p className="feature-description">
                Get a comprehensive, Scripture-faithful study guide with insights and applications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="info-section alt-section">
        <div className="container">
          <h2 className="section-title">Who It's For</h2>
          <div className="audience-grid">
            <Card className="audience-card">
              <CardHeader>
                <Users className="audience-icon" />
                <CardTitle className="audience-title">Small Group Leaders</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="audience-description">
                  Prepare engaging Bible studies for your group in minutes, not hours.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="audience-card">
              <CardHeader>
                <BookOpen className="audience-icon" />
                <CardTitle className="audience-title">Individual Believers</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="audience-description">
                  Deepen your personal devotional time with structured, meaningful insights.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="audience-card">
              <CardHeader>
                <Sparkles className="audience-icon" />
                <CardTitle className="audience-title">Pastors & Teachers</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="audience-description">
                  Generate sermon outlines and deep theological studies with word studies.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="info-section">
        <div className="container">
          <h2 className="section-title">Roadmap</h2>
          <div className="roadmap-grid">
            <div className="roadmap-item">
              <Map className="roadmap-icon current" />
              <h3 className="roadmap-title">Current - MVP</h3>
              <ul className="roadmap-list">
                <li>AI-powered study generation</li>
                <li>Multiple Bible versions</li>
                <li>Adjustable study length & type</li>
                <li>Multilingual support</li>
                <li>Sermon mode with word studies</li>
                <li>User accounts & preferences</li>
                <li>Daily notifications</li>
              </ul>
            </div>
            <div className="roadmap-item">
              <Map className="roadmap-icon future" />
              <h3 className="roadmap-title">Coming Soon</h3>
              <ul className="roadmap-list">
                <li>Save and organize studies</li>
                <li>Share studies with others</li>
                <li>PDF export option</li>
                <li>Cross-reference linking</li>
              </ul>
            </div>
            <div className="roadmap-item">
              <Map className="roadmap-icon future" />
              <h3 className="roadmap-title">Future Vision</h3>
              <ul className="roadmap-list">
                <li>Community study library</li>
                <li>Interactive study groups</li>
                <li>Mobile app</li>
                <li>Audio study generation</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Unit - Above Footer */}
      <section style={{ padding: '3rem 0', background: '#F7F7F7' }}>
        <div className="container">
          <AdSense 
            slot="2222222222"
            format="horizontal"
            responsive={true}
            className="footer-ad"
            label={false}
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img 
                src="/its-written-logo.png" 
                alt="Its Written AI Logo" 
                style={{ height: '36px', width: 'auto' }}
              />
              <span className="footer-brand-text">Its Written AI</span>
            </div>
            <p className="footer-text">
              AI-powered Bible study generator. Because It Is Written. Built with love for the Body of Christ.
            </p>
            <button 
              onClick={() => setShowDonationModal(true)}
              className="footer-donate-link"
            >
              <Heart style={{ width: '14px', height: '14px', marginRight: '0.25rem' }} />
              Support Its Written AI
            </button>
            <p className="footer-copyright">
              © 2025 Its Written AI – All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
